import { Repository } from "./0Repository";
import { App as AppEntity } from "../Entity/App";
import { HAApp } from "../Entity/HAApp";
import { HANavigationApp } from "../Entity/HANavigationApp";
import { FSApp } from "../Entity/FSApp";
import { toMap } from "../../../HA";

export class App extends Repository {

    public findNavigationById(data: Map<any, any>) {
        return this.findById(data, true);
    }

    public findById(data: Map<any, any>, isNavigation?: boolean): Promise<any> {

        if (this.isCached(data)) {
            return Promise.resolve(this.getFromCache(data));
        }

        if (data.size < 1) {
            return Promise.reject("no app id.");
        }

        const reqData: Record<string, any> = {};
        data.forEach((v: any, n: any) => { reqData[n] = v; });
        if (!reqData["_url"] && typeof window !== "undefined" && window.location) {
            reqData["_url"] = window.location.pathname + window.location.search;
        }
        // ha/508 — Ne pas écraser un `d` explicite (paramètres repliés depuis le
        // href du lien cliqué, p.ex. `?reference=D-100010`) par la query de la
        // page COURANTE. Le fallback window.location ne sert que pour le boot /
        // les navs qui ne portent pas leurs propres paramètres.
        if (!reqData["d"] && typeof window !== "undefined" && window.location.search) {
            const qp: Record<string, string> = {};
            new URLSearchParams(window.location.search).forEach((v, k) => { qp[k] = v; });
            if (Object.keys(qp).length > 0) {
                reqData["d"] = JSON.stringify(qp);
            }
        }

        return this.request(toMap({url: "/ha/app", data: reqData}))
            .then((response: Map<any, any>) => {
                const elementData: Map<any, any> = toMap(response.get("app"));
                let app: AppEntity | HAApp | HANavigationApp | FSApp = null;
                if (elementData && response.get("success") === true) {
                    let aid = elementData.get("id");
                    if (!aid) {
                        aid = data.get("id");
                    } else {
                        data.set("id", aid);
                    }
                    if (data.has("_id") && !elementData.has("_id")) {
                        elementData.set("_id", data.get("_id"));
                    }
                    const appData = toMap({
                        id: aid,
                        services: this.getAppServices(),
                    }, elementData);

                    if (appData.has("navigationData")) {
                        return this.findById(toMap(appData.get("navigationData") as any), true).then(
                            (navApp: AppEntity) => {
                                appData.set("navigation", navApp);
                                app = new HAApp(appData as Map<string, any>);
                                this.cache(data, app);
                                return app;
                            });
                    } else {
                        if (data.has("_isModal") || appData.has("_isModal") || appData.has("noNav")) {
                            app = new FSApp(appData as Map<string, any>);
                        } else {
                            app = isNavigation ? new HANavigationApp(appData as Map<string, any>) : new AppEntity(appData as Map<string, any>);
                        }
                        this.cache(data, app);
                        return app;
                    }

                } else {
                    // Navigation avec un token CSRF périmé (onglet resté ouvert
                    // pendant un re-login ailleurs) : la SPA ne peut pas récupérer
                    // en AJAX — on laisse le serveur re-servir la page PLEINE
                    // (session + meta csrf frais). Garde anti-boucle : jamais de
                    // reload si la cible est déjà la page courante (échec au boot).
                    const target = reqData["_url"];
                    if (response.get("_httpStatus") === 403
                        && target && typeof window !== "undefined"
                        && target !== window.location.pathname + window.location.search
                    ) {
                        window.location.assign(target);
                        return new Promise(() => undefined); // navigation pleine page en cours
                    }
                    return Promise.reject(response);
                }
            });
    }

    public createApp(
        data: Map<any, any>,
        elementData: Map<any, any>,
        isNavigation?: boolean,
    ): AppEntity | HAApp | HANavigationApp | FSApp {

        let app: AppEntity | HAApp | HANavigationApp | FSApp;

        let aid = elementData.get("id");
        if (!aid) {
            aid = data.get("id");
        } else {
            data.set("id", aid);
        }
        if (data.has("_id") && !elementData.has("_id")) {
            elementData.set("_id", data.get("_id"));
        }
        const appData = toMap({
            id: aid,
            services: this.getAppServices(),
        }, elementData);

        if (data.has("_isModal") || appData.has("_isModal") || appData.has("noNav")) {
            app = new FSApp(appData as Map<string, any>);
        } else {
            app = isNavigation ? new HANavigationApp(appData as Map<string, any>) : new AppEntity(appData as Map<string, any>);
        }
        this.cache(data, app);
        return app;
    }

    public remove(id: string) {
        // ha/508 — Les clés sont composites (`id` ou `id::<d>`). Purger toutes
        // les variantes d'un même pageId, pas seulement l'entrée nue.
        const cache = this.getCache();
        const prefix = id + "::";
        for (const key of Array.from(cache.keys())) {
            if (key === id || (typeof key === "string" && key.startsWith(prefix))) {
                cache.delete(key);
            }
        }
    }

    /**
     * ha/508 — Une page détail (`devis-detail`, `client-detail`…) partage son
     * pageId entre tous les enregistrements ; la clé nue les ferait collisionner
     * (le 2ᵉ enregistrement servirait le cache du 1ᵉʳ). On discrimine par `d`
     * (paramètres SPA repliés depuis le href, p.ex. `{"reference":"D-100010"}`).
     * Sans `d`, clé nue → comportement historique inchangé.
     */
    protected cacheKey(data: Map<any, any>): any {
        const id = data.get("id") ?? null;
        if (id === null) { return data.get("_id") ?? null; }
        // `_id` = clé d'instance composite (posée par Anchor, `<pageId>::<d>`) ;
        // sinon dérivée de `d` ; sinon le pageId nu (comportement historique).
        const explicit = data.get("_id");
        if (explicit) { return explicit; }
        const d = data.get("d");
        return d ? id + "::" + d : id;
    }

    protected getAppServices() {
        return {
            "ha.service.appManager": this.get("ha.service.appManager"),
            "ha.service.user": this.get("ha.service.user"),
            "ha.service.webpage": this.get("ha.service.webpage"),
        };
    }
}
